import { z } from "zod";
import {
  PaginationRequestSchema,
  PaginationResponseSchema,
} from "./pagination";
import { TournamentWinnerConfigSchema } from "./tournamentWinnerPrediction";

// ======================
// SCORING TYPES (Compartidos entre frontend y backend)
// ======================
export type ScoringRuleType =
  | "exact_score"
  | "winner"
  | "bracket_winner"
  | "goal_difference"
  | "home_goals_exact"
  | "away_goals_exact"
  | "unique_bonus";

export interface ResultRule {
  type: ScoringRuleType;
  points: number;
  priority?: number;
}

export interface RuleCondition {
  type: ScoringRuleType;
  isUniqueHit?: boolean;
}

export interface BonusRule {
  type: ScoringRuleType;
  points: number;
  requires?: ScoringRuleType[];
  condition?: RuleCondition;
}

export interface DeadlineConfig {
  hours?: number;
  minutes?: number;
  days?: number;
}

export interface PoolConfig {
  scoring: {
    resultRules: ResultRule[];
    bonusRules: BonusRule[];
    defaultPoints: number;
    predictionDeadline?: DeadlineConfig;
  };
  privacity?: {
    showOthersPredictions: "always" | "after_lock" | "never";
  };
  tournamentWinner?: {
    enabled: boolean;
    points: number;
    deadlineAt?: string | Date | null;
    deadlineMinutesBeforeFirstMatch?: number;
  };
}

export type ScoringConfig = PoolConfig["scoring"];

export interface MatchResult {
  homeGoals: number;
  awayGoals: number;
  winner: "HOME" | "AWAY" | "DRAW";
}

export interface Prediction {
  homeGoals: number;
  awayGoals: number;
  predicted_winner?: "HOME" | "AWAY" | "DRAW" | null;
}

export interface EvaluationContext {
  isUniqueExactWinner?: boolean;
}

export const DEFAULT_POOL_CONFIG: PoolConfig = {
  scoring: {
    resultRules: [
      { type: "exact_score", points: 5, priority: 1 },
      { type: "winner", points: 3, priority: 2 },
    ],
    bonusRules: [
      {
        type: "home_goals_exact",
        points: 1,
        requires: ["winner"],
      },
      {
        type: "away_goals_exact",
        points: 1,
        requires: ["winner"],
      },
      {
        type: "unique_bonus",
        points: 2,
        condition: {
          type: "exact_score",
          isUniqueHit: true,
        },
      },
    ],
    defaultPoints: 0,
    predictionDeadline: {
      hours: 1,
    },
  },
  privacity: {
    showOthersPredictions: "after_lock",
  },
  tournamentWinner: {
    enabled: false,
    points: 10,
    deadlineAt: null,
  },
};

// ======================
// SCORING ZOD SCHEMAS
// ======================
const SCORING_RULE_TYPE_VALUES = [
  "exact_score_unique",
  "exact_score",
  "winner_or_draw",
  "winner",
  "bracket_winner",
  "unique_bonus",
  "goal_difference",
  "home_goals_exact",
  "away_goals_exact",
] as const;

export const ResultRuleSchema = z.object({
  type: z.enum(SCORING_RULE_TYPE_VALUES),
  points: z.number(),
  priority: z.number().int().optional(),
});

export const RuleConditionSchema = z.object({
  type: z.enum(SCORING_RULE_TYPE_VALUES),
  isUniqueHit: z.boolean().optional(),
});

export const BonusRuleSchema = z.object({
  type: z.enum(SCORING_RULE_TYPE_VALUES),
  points: z.number(),
  requires: z.array(z.enum(SCORING_RULE_TYPE_VALUES)).optional(),
  condition: RuleConditionSchema.optional(),
});

export const PoolConfigSchema = z.object({
  scoring: z.object({
    resultRules: z.array(ResultRuleSchema),
    bonusRules: z.array(BonusRuleSchema),
    defaultPoints: z.number(),
    predictionDeadline: z
      .object({
        hours: z.number().int().min(0).optional(),
        minutes: z.number().int().min(0).optional(),
        days: z.number().int().min(0).optional(),
      })
      .optional(),
  }),
  privacity: z
    .object({
      showOthersPredictions: z.enum(["always", "after_lock", "never"]),
    })
    .optional(),
  tournamentWinner: TournamentWinnerConfigSchema.optional(),
});

// ======================
// BETTING POOL STATUS QUERY
// ======================
export const BettingPoolStatusQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
  slug: z.string().optional(),
});
export type BettingPoolStatusQueryDTO = z.infer<
  typeof BettingPoolStatusQuerySchema
>;

// ======================
// BETTING POOL QUERY
// ======================
export const BettingPoolQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
  creatorId: z.string().optional(),
});
export type BettingPoolQueryDTO = z.infer<typeof BettingPoolQuerySchema>;

// ======================
// BETTING POOL STATUS
// ======================
export const BettingPoolStatusResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  color: z.string().nullable(),
});
export type BettingPoolStatusResponseDTO = z.infer<
  typeof BettingPoolStatusResponseSchema
>;

export const BettingPoolStatusesPaginatedResponseSchema =
  PaginationResponseSchema(BettingPoolStatusResponseSchema);
export type BettingPoolStatusesPaginatedResponseDTO = z.infer<
  typeof BettingPoolStatusesPaginatedResponseSchema
>;

export const CreateBettingPoolStatusRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  slug: z.string().min(1, "El slug es requerido"),
  color: z.string().optional().nullable(),
});
export type CreateBettingPoolStatusRequestDTO = z.infer<
  typeof CreateBettingPoolStatusRequestSchema
>;

export const UpdateBettingPoolStatusRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").optional(),
  slug: z.string().min(1, "El slug es requerido").optional(),
  color: z.string().optional().nullable(),
});
export type UpdateBettingPoolStatusRequestDTO = z.infer<
  typeof UpdateBettingPoolStatusRequestSchema
>;

// ======================
// BETTING POOLS
// ======================
const BettingPoolPrizeTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

export const BettingPoolPrizePositionSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

export const BettingPoolPrizeInputSchema = z.object({
  position: BettingPoolPrizePositionSchema,
  prizeTypeId: z.string().min(1, "El tipo de premiación es requerido"),
  prizeValue: z.string().max(255).min(1, "Indica el valor del premio"),
  prizeImageUrl: z.string().optional().nullable(),
});
export type BettingPoolPrizeInputDTO = z.infer<typeof BettingPoolPrizeInputSchema>;

export const BettingPoolPrizeResponseSchema = z.object({
  position: BettingPoolPrizePositionSchema,
  prizeTypeId: z.string(),
  prizeValue: z.string().nullable(),
  prizeImageUrl: z.string().nullable(),
  prizeType: BettingPoolPrizeTypeSchema.nullable(),
});
export type BettingPoolPrizeResponseDTO = z.infer<typeof BettingPoolPrizeResponseSchema>;

const BettingPoolPrizesInputSchema = z
  .array(BettingPoolPrizeInputSchema)
  .min(1, "Debe configurar al menos el premio de 1er puesto")
  .max(3)
  .refine(
    (prizes) => new Set(prizes.map((prize) => prize.position)).size === prizes.length,
    "No puede haber posiciones de premiación duplicadas",
  )
  .refine(
    (prizes) => prizes.some((prize) => prize.position === 1),
    "El premio de 1er puesto es obligatorio",
  );

export const BettingPoolResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  inviteCode: z.string(),
  creatorId: z.string(),
  seasonId: z.string(),
  statusId: z.string(),
  isPrivate: z.boolean(),
  isJoinable: z.boolean(),
  isPredictable: z.boolean(),
  poolConfig: PoolConfigSchema,
  prizes: z.array(BettingPoolPrizeResponseSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable(),
});
export type BettingPoolResponseDTO = z.infer<typeof BettingPoolResponseSchema>;

export const BettingPoolsPaginatedResponseSchema = PaginationResponseSchema(
  BettingPoolResponseSchema,
);
export type BettingPoolsPaginatedResponseDTO = z.infer<
  typeof BettingPoolsPaginatedResponseSchema
>;

export const BettingPoolWithDetailsResponseSchema =
  BettingPoolResponseSchema.extend({
    creator: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable(),
    status: z
      .object({
        id: z.string(),
        name: z.string(),
        slug: z.string(),
        color: z.string().nullable(),
      })
      .nullable(),
    season: z
      .object({
        id: z.string(),
        season: z.string(),
        isCurrent: z.boolean(),
      })
      .nullable(),
    tournament: z
      .object({
        id: z.string(),
        name: z.string(),
        logo: z.string().nullable(),
      })
      .nullable(),
    participantsCount: z.number(),
  });
export type BettingPoolWithDetailsResponseDTO = z.infer<
  typeof BettingPoolWithDetailsResponseSchema
>;

export const BettingPoolsWithDetailsPaginatedResponseSchema =
  PaginationResponseSchema(BettingPoolWithDetailsResponseSchema);
export type BettingPoolsWithDetailsPaginatedResponseDTO = z.infer<
  typeof BettingPoolsWithDetailsPaginatedResponseSchema
>;

export const BettingPoolWithTournamentResponseSchema =
  BettingPoolResponseSchema.extend({
    creator: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable(),
    status: z
      .object({
        id: z.string(),
        name: z.string(),
        slug: z.string(),
        color: z.string().nullable(),
      })
      .nullable(),
    season: z
      .object({
        id: z.string(),
        season: z.string(),
        isCurrent: z.boolean(),
      })
      .nullable(),
    tournament: z
      .object({
        id: z.string(),
        name: z.string(),
        logo: z.string().nullable(),
      })
      .nullable(),
    participantsCount: z.number(),
  });
export type BettingPoolWithTournamentResponseDTO = z.infer<
  typeof BettingPoolWithTournamentResponseSchema
>;

export const CreateBettingPoolRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  description: z.string().optional().nullable(),
  seasonId: z.string().min(1, "La temporada es requerida"),
  prizes: BettingPoolPrizesInputSchema,
  isPrivate: z.boolean().optional().default(false),
  poolConfig: PoolConfigSchema.optional(),
});
export type CreateBettingPoolRequestDTO = z.infer<
  typeof CreateBettingPoolRequestSchema
>;

export const UpdateBettingPoolRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").optional(),
  description: z.string().optional().nullable(),
  inviteCode: z.string().min(1, "El código es requerido").optional(),
  prizes: BettingPoolPrizesInputSchema.optional(),
  isPrivate: z.boolean().optional(),
  isJoinable: z.boolean().optional(),
  isPredictable: z.boolean().optional(),
  poolConfig: PoolConfigSchema.optional(),
});
export type UpdateBettingPoolRequestDTO = z.infer<
  typeof UpdateBettingPoolRequestSchema
>;

export const UpdateBettingPoolIsJoinableRequestSchema = z.object({
  isJoinable: z.boolean(),
});
export type UpdateBettingPoolIsJoinableRequestDTO = z.infer<
  typeof UpdateBettingPoolIsJoinableRequestSchema
>;

export const UpdateBettingPoolIsPredictableRequestSchema = z.object({
  isPredictable: z.boolean(),
});
export type UpdateBettingPoolIsPredictableRequestDTO = z.infer<
  typeof UpdateBettingPoolIsPredictableRequestSchema
>;

export const ChangePoolStatusRequestSchema = z.object({
  statusId: z.string().min(1, "El estado es requerido"),
});
export type ChangePoolStatusRequestDTO = z.infer<
  typeof ChangePoolStatusRequestSchema
>;
